import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../common/Layout";
import { usePageMeta } from "../common/usePageMeta";

export default function NotifyPage() {
  usePageMeta({ title: 'Get Notified | TearDrop Hydro', path: '/notify', noindex: true });
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const itemName = params.get("item") || "this product";
  const status   = params.get("status");
  const isSoon   = status === "COMING_SOON";

  const [email,     setEmail]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("https://teardrophydro.co.uk/garageFarm/notifyProduct", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, product: itemName }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        throw new Error("Failed");
      }
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="notify-shell">

        <motion.div
          className="notify-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
        >
          {/* Back link */}
          <button className="notify-back" onClick={() => navigate(-1)}>
            ← Back
          </button>

          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>

                {/* Header */}
                <div className="notify-header">
                  <span className="notify-emoji" aria-hidden="true">
                    {isSoon ? "🌱" : "📦"}
                  </span>
                  <p className="eyebrow">{isSoon ? "Coming Soon" : "Out of Stock"}</p>
                  <h1 className="h2 notify-heading">
                    {isSoon ? "Join the Waitlist" : "Get Restock Alert"}
                  </h1>
                  <p className="muted notify-sub">
                    {isSoon
                      ? `${itemName} is currently growing. Be first to know when the harvest is ready.`
                      : `${itemName} is temporarily out of stock. We'll email you the moment it's back.`}
                  </p>
                </div>

                {/* Product pill */}
                <div className="notify-product-pill">
                  <span className="notify-product-dot" aria-hidden="true" />
                  <span>{itemName}</span>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="notify-form" noValidate>
                  <label className="notify-label" htmlFor="notify-email">
                    Email address
                  </label>
                  <input
                    id="notify-email"
                    type="email"
                    required
                    placeholder="hello@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="nl-input notify-input"
                  />
                  <button
                    type="submit"
                    className="btn btn-primary notify-btn"
                    disabled={loading}
                  >
                    {loading ? "Saving…" : isSoon ? "Keep me updated 🌿" : "Notify me when back"}
                  </button>
                </form>

                <p className="notify-reassurance">
                  No spam. One email, when it's ready.
                </p>

              </motion.div>
            ) : (
              <motion.div
                key="success"
                className="notify-success"
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
              >
                <div className="success-state-icon">✓</div>
                <h2 className="h2">You're on the list!</h2>
                <p className="muted">
                  We'll email <strong>{email}</strong> as soon as {itemName} is ready.
                </p>
                <button
                  className="btn btn-ghost"
                  onClick={() => navigate("/browse-products")}
                  style={{ marginTop: "var(--s-5)" }}
                >
                  Browse other products
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

      </div>

      <style>{`
        .notify-shell {
          min-height: 80vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--s-6) var(--s-5);
          background: var(--bg);
        }

        .notify-card {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: var(--r-2xl);
          padding: var(--s-7) var(--s-6);
          box-shadow: var(--shadow-1);
          width: 100%;
          max-width: 440px;
        }

        .notify-back {
          font-size: var(--fs-13);
          color: var(--muted);
          margin-bottom: var(--s-6);
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: color .15s ease;
        }
        .notify-back:hover { color: var(--text); }

        .notify-header {
          text-align: center;
          margin-bottom: var(--s-5);
        }

        .notify-emoji {
          font-size: 3rem;
          line-height: 1;
          display: block;
          margin-bottom: var(--s-3);
          animation: notifyBounce .55s cubic-bezier(.34,1.56,.64,1) both;
        }
        @keyframes notifyBounce {
          from { opacity: 0; transform: translateY(-12px) scale(.8); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .notify-heading {
          margin-bottom: var(--s-2) !important;
          color: var(--brand);
        }

        .notify-sub {
          font-size: var(--fs-14);
          line-height: var(--lh-loose);
          max-width: 30ch;
          margin: 0 auto;
        }

        .notify-product-pill {
          display: inline-flex;
          align-items: center;
          gap: var(--s-2);
          padding: var(--s-2) var(--s-3);
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 999px;
          font-size: var(--fs-13);
          font-weight: 600;
          color: var(--text);
          margin: 0 auto var(--s-5);
          display: flex;
          width: fit-content;
        }

        .notify-product-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--brand-2);
          flex-shrink: 0;
        }

        .notify-form {
          display: grid;
          gap: var(--s-3);
        }

        .notify-label {
          font-size: var(--fs-12);
          font-weight: 700;
          letter-spacing: .1em;
          text-transform: uppercase;
          color: var(--muted);
        }

        .notify-input {
          width: 100%;
          font-size: var(--fs-16);
          padding: var(--s-3) var(--s-4);
          transition: border-color .15s, box-shadow .15s;
        }
        .notify-input:focus {
          border-color: var(--brand-2);
          box-shadow: 0 0 0 3px color-mix(in oklab, var(--brand-2) 20%, transparent);
          outline: none;
        }

        .notify-btn {
          width: 100%;
          min-height: 50px;
          font-size: var(--fs-16);
          border-radius: var(--r-xl);
        }

        .notify-reassurance {
          text-align: center;
          font-size: var(--fs-12);
          color: var(--muted);
          margin-top: var(--s-3);
          margin-bottom: 0;
        }

        .notify-success {
          text-align: center;
          padding: var(--s-4) 0;
        }
        .notify-success p {
          font-size: var(--fs-15);
          line-height: var(--lh-loose);
        }
      `}</style>
    </Layout>
  );
}